import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import differenceInDays from 'date-fns/differenceInDays'
import { debounce } from 'lodash'
import {
  all,
  call,
  put,
  takeEvery,
  select,
  takeLatest
} from 'redux-saga/effects'

import apiConfig from '../../../api/AnalyticsApiHelpers/apiConfig'
import BusinessUnitsApi from '../../../api/BusinessUnitsApi'
import Errors from '../../../constants/Errors'
import { EventsUtil, categories, actions, labels } from '../../../helpers/trackingAnalytics/events'
import localize from '../../Localize/localize'
import PaymentsResource from '../api/dashboardAnalyticsApi'
import { getGranularityConfigurationByDays } from '../granularityConfig'
import {
  FETCH_DASHBOARD,
  ON_PAYMENT_CHANGE,
  ON_DATE_CHANGE,
  ON_COUNTRY_CHANGE,
  ON_APP_CHANGE,
  ON_GRANULARITY_CHANGE,
  ON_METRICS_CHANGE,
  ON_BARS_CHANGE,
  selectGranularity,
  fetchApps,
  appsFetchSuccess,
  fetchBars,
  barsFetchSuccess,
  fetchCountries,
  countriesFetchSuccess,
  fetchGraph,
  graphFetchSuccess,
  fetchPaymentflow,
  paymentflowFetchSuccess,
  selectPayment,
  selectDate,
  selectCountries,
  selectApp,
  selectMetrics,
  selectBar
} from './actions'
import {
  getDatesData,
  getPaymentStage,
  selectedApps,
  getSelectedCountries,
  getMetrics,
  getSelectedBar
} from './selectors'
import messageDispatcher from '../../Messages/redux/messageDispatcher'

const SENDEVENT_APPS_DELAY = 5000

// Sagas
export function * fetchAppsSaga () {
  try {
    yield put(fetchApps())
    const apps = yield call(BusinessUnitsApi.fetchBusinessUnits)
    yield put(appsFetchSuccess(apps || []))
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

function * fetchCountriesSaga () {
  try {
    yield put(fetchCountries())
    const { from, to } = yield select(getDatesData)
    const dimension = apiConfig.dimensions.country
    const countries = yield call(PaymentsResource.getFilterData, { from, to, dimension })
    yield put(countriesFetchSuccess(countries))
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

function * fetchGraphSaga () {
  try {
    yield put(fetchGraph())
    const { from, to, granularity } = yield select(getDatesData)
    const paymentStage = yield select(getPaymentStage)
    const apps = yield select(selectedApps)
    const countries = yield select(getSelectedCountries)
    const metrics = yield select(getMetrics)
    const dimension = yield select(getSelectedBar)
    const graph = yield call(
      PaymentsResource.getGraphData,
      {
        from,
        to,
        granularity,
        paymentStage,
        apps,
        countries,
        metrics,
        dimension
      }
    )
    yield put(graphFetchSuccess(graph))
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

function * fetchBarsSaga () {
  try {
    yield put(fetchBars())
    const { from, to } = yield select(getDatesData)
    const paymentStage = yield select(getPaymentStage)
    const apps = yield select(selectedApps)
    const countries = yield select(getSelectedCountries)
    const metrics = yield select(getMetrics)
    const bars = yield call(PaymentsResource.getBarsBreakdownData, { from, to, paymentStage, apps, countries, metrics })
    yield put(barsFetchSuccess(bars))
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

function * fetchPaymentFlowSaga () {
  try {
    yield put(fetchPaymentflow())
    const { from, to } = yield select(getDatesData)
    const apps = yield select(selectedApps)
    const countries = yield select(getSelectedCountries)
    const metrics = yield select(getMetrics)
    const payFlow = yield call(PaymentsResource.getPaymentActionsData, { from, to, apps, countries, metrics })
    yield put(paymentflowFetchSuccess(payFlow))
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

function * fetchFilteredData () {
  yield all([
    call(fetchGraphSaga),
    call(fetchCountriesSaga),
    call(fetchBarsSaga),
    call(fetchPaymentFlowSaga)
  ])
}

function * hydrateDashboardPage () {
  yield all([
    call(fetchAppsSaga),
    call(fetchCountriesSaga),
    call(fetchGraphSaga),
    call(fetchBarsSaga),
    call(fetchPaymentFlowSaga)
  ])
}

export function * dashboardSaga () {
  try {
    yield call(hydrateDashboardPage)
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectPaymentSaga (action) {
  try {
    yield call(
      EventsUtil.sendEvent,
      {
        category: categories.dashboard,
        action: actions.payment_stage,
        label: action.paymentStage
      }
    )
    yield put(selectPayment(action.paymentStage))
    yield fetchFilteredData()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectDateSaga (action) {
  try {
    const { from, to, selectedLabel } = action
    const differenceDays = differenceInCalendarDays(to, from)
    yield call(
      EventsUtil.sendEvent,
      {
        category: categories.dashboard,
        action: actions.filter,
        label: selectedLabel,
        value: differenceDays
      }
    )

    if (from && to) {
      yield put(selectDate(action))
      let { granularity } = yield select(getDatesData)
      if (
        differenceInDays(to, from) > 30 &&
        ![apiConfig.granularity.week, apiConfig.granularity.day].includes(granularity)
      ) {
        granularity = apiConfig.granularity.month
      } else {
        const granularityConfiguration = getGranularityConfigurationByDays(differenceDays)
        if (!granularityConfiguration.granularities.includes(granularity)) {
          granularity = granularityConfiguration.defaultGranularity
        }
      }
      yield put(selectGranularity(granularity))
      yield fetchFilteredData()
    }
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectCountries ({ countries }) {
  try {
    yield call(
      EventsUtil.sendEvent,
      {
        category: categories.dashboard,
        action: actions.filter,
        label: labels.ip_country,
        value: countries.length
      }
    )
    yield put(selectCountries(countries))
    yield fetchFilteredData()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectApp ({ apps }) {
  const sendEvent = debounce((apps) => {
    EventsUtil.sendEvent({
      category: categories.dashboard,
      action: actions.filter,
      label: labels.business_units,
      value: apps.length
    })
  }, SENDEVENT_APPS_DELAY)

  try {
    yield call(sendEvent, apps)
    yield put(selectApp(apps))
    yield fetchFilteredData()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectGranularity ({ granularity }) {
  try {
    yield call(
      EventsUtil.sendEvent,
      {
        category: categories.dashboard,
        action: actions.granularity,
        label: granularity
      }
    )
    yield put(selectGranularity(granularity))
    yield fetchGraphSaga()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectMetrics ({ metrics }) {
  try {
    const metricsLabel = metrics === apiConfig.metrics.totalAmount ? labels.amount : labels.transactions
    yield call(
      EventsUtil.sendEvent,
      {
        category: categories.dashboard,
        action: actions.data_type_view,
        label: metricsLabel
      }
    )
    yield put(selectMetrics(metrics))
    yield fetchFilteredData()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * dashboardSelectBar ({ bar }) {
  try {
    yield put(selectBar(bar))
    yield fetchGraphSaga()
  } catch (err) {
    messageDispatcher.addError({
      text: Errors.defaultError(localize.translate),
      err
    })
  }
}

export function * registerSagas () {
  yield takeLatest(FETCH_DASHBOARD, dashboardSaga)
  yield takeLatest(ON_PAYMENT_CHANGE, dashboardSelectPaymentSaga)
  yield takeLatest(ON_DATE_CHANGE, dashboardSelectDateSaga)
  yield takeEvery(ON_COUNTRY_CHANGE, dashboardSelectCountries)
  yield takeEvery(ON_APP_CHANGE, dashboardSelectApp)
  yield takeLatest(ON_GRANULARITY_CHANGE, dashboardSelectGranularity)
  yield takeLatest(ON_METRICS_CHANGE, dashboardSelectMetrics)
  yield takeLatest(ON_BARS_CHANGE, dashboardSelectBar)
}
